import { useCallback, useEffect, useState } from 'react';
import { T } from '../components/text/TextEdit';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { Button } from './ui/button';
import { SectionCard, Skeleton } from './pipeline/ui';

// Per-industry corpus access toggle (free / paywalled). Rows derive from the live datasets
// (industry + lang) plus any already-configured corpus_access entries. Default free.
export default function CorpusAccessCard({ industriesByLang = {} }) {
  const { t } = useTranslation();
  const [access, setAccess] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState('');

  const load = useCallback(() => {
    api.tenantAdmin('list_corpus_access')
      .then((r) => {
        const map = {};
        for (const a of (r.access || [])) map[`${a.industry_key}|${a.corpus_lang}`] = a.access_mode;
        setAccess(map);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const rows = [];
  for (const [lang, inds] of Object.entries(industriesByLang)) {
    for (const industry of inds) rows.push({ industry, lang });
  }

  const toggle = async (industry, lang) => {
    const key = `${industry}|${lang}`;
    const next = access[key] === 'paywalled' ? 'free' : 'paywalled';
    setSavingKey(key);
    try {
      await api.tenantAdmin('set_corpus_access', { industry_key: industry, corpus_lang: lang, tier: null, access_mode: next });
      setAccess((m) => ({ ...m, [key]: next }));
      toast.success(t('corpusAccess.saved'));
    } catch (e) { toast.error(e.message); } finally { setSavingKey(''); }
  };

  return (
    <SectionCard title={t('corpusAccess.title')} testId="corpus-access-card">
      <p className="text-sm text-muted-foreground -mt-2"><T k="corpusAccess.hint" /></p>
      {loading ? <Skeleton className="h-24 w-full" /> : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="corpus-access-empty"><T k="corpusAccess.empty" /></p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="corpus-access-table">
            <thead>
              <tr className="text-start text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                <th className="text-start py-2 pe-4"><T k="corpusAccess.colIndustry" /></th>
                <th className="text-start py-2 pe-4"><T k="corpusAccess.colLang" /></th>
                <th className="text-start py-2 pe-4"><T k="corpusAccess.colMode" /></th>
                <th className="text-start py-2"></th>
              </tr>
            </thead>
            <tbody className="font-data">
              {rows.map(({ industry, lang }) => {
                const key = `${industry}|${lang}`;
                const paywalled = access[key] === 'paywalled';
                return (
                  <tr key={key} className="border-b border-border" data-testid={`corpus-access-row-${industry}-${lang}`}>
                    <td className="py-2 pe-4 text-foreground">{industry}</td>
                    <td className="py-2 pe-4">{lang}</td>
                    <td className="py-2 pe-4">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs border ${paywalled ? 'bg-warning-soft text-warning border-warning-border' : 'bg-success-soft text-success border-success-border'}`} data-testid={`corpus-access-mode-${industry}-${lang}`}>
                        {paywalled ? t('corpusAccess.paywalled') : t('corpusAccess.free')}
                      </span>
                    </td>
                    <td className="py-2 text-end">
                      <Button variant="ghost" size="sm" disabled={savingKey === key} onClick={() => toggle(industry, lang)} data-testid={`corpus-access-toggle-${industry}-${lang}`}>
                        {paywalled ? t('corpusAccess.makeFree') : t('corpusAccess.makePaywalled')}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </SectionCard>
  );
}
